import { useMemo, useState } from 'react';

import SimpleModalInfo, {
  SimpleModalInfoTitle,
} from 'lib/modal/simple-modal-info';
import {
  AbbrStyled,
  Heading16,
  PStyled,
} from 'approot/shared/styles/typography.style';
import { BadgeSmallStyled } from 'approot/shared/badge/badge.style';
import {
  OutcomeLinkList,
  OutcomeList,
  OutcomesCol,
  OutcomesRow,
} from 'approot/shared/outcomes/outcomes-shared.style';
import { UnitOutcomesList } from 'approot/shared/outcomes/unit-outcomes-list';
import { OUTCOME_MODAL_SIZE } from 'approot/shared/outcomes/outcome.constants';
import {
  ENGLISH_CONNECTION,
  MATH_CONNECTION,
  TeachingAndLearningOutcome,
  TeachingLearningConnections,
} from './teaching-learning-sequence.constants';
import { TeachingLearningSequenceViewTitle } from './teaching-learning-sequence.style';
import { ENGLISH } from 'global/constants';
import { InquisitiveSubject } from 'global/graphql/scalars.type';

const TeachingAndLearningOutcomesView = ({
  connections,
}: {
  connections: TeachingLearningConnections[];
}) => {
  const [outcomeId, setOutcomeId] = useState<number | undefined>();

  const selected = useMemo(() => {
    let selected: {
      subject: InquisitiveSubject;
      outcome: TeachingAndLearningOutcome | null;
    } = {
      subject: ENGLISH,
      outcome: null,
    };
    connections.forEach(connection => {
      const selectedOutcome = connection.outcomes?.find(
        o => o.id === outcomeId
      );
      if (selectedOutcome) {
        selected.subject = connection.subject as InquisitiveSubject;
        selected.outcome = selectedOutcome;
      }
    });
    return selected;
  }, [connections, outcomeId]);

  if (connections.every(conn => conn.outcomes?.length === 0)) {
    return null;
  }

  return (
    <>
      {selected.outcome && (
        <SimpleModalInfo
          title={
            selected.subject === ENGLISH ? ENGLISH_CONNECTION : MATH_CONNECTION
          }
          titleElement={
            <SimpleModalInfoTitle
              title={
                selected.subject === ENGLISH
                  ? ENGLISH_CONNECTION
                  : MATH_CONNECTION
              }
            />
          }
          isVisible
          size={OUTCOME_MODAL_SIZE}
          dataTracking={{
            label: 'outcome-connections',
          }}
          onClose={() => setOutcomeId(undefined)}
        >
          <Heading16 as="h2" marginBottom={4}>
            {selected.outcome.code}
          </Heading16>
          <PStyled>{selected.outcome.description}</PStyled>
          <OutcomeLinkList>
            <li>
              <BadgeSmallStyled>{selected.outcome.category}</BadgeSmallStyled>
            </li>
            <li>
              <BadgeSmallStyled>{selected.outcome.type}</BadgeSmallStyled>
            </li>
          </OutcomeLinkList>
        </SimpleModalInfo>
      )}
      <TeachingLearningSequenceViewTitle>
        <AbbrStyled
          aria-label="English Language Arts"
          title="English Language Arts"
        >
          ELA
        </AbbrStyled>{' '}
        and Math Connections
      </TeachingLearningSequenceViewTitle>
      <OutcomesRow>
        {connections.map((connection, index) =>
          connection.outcomes && connection.outcomes.length > 0 ? (
            <OutcomesCol key={index} xs={6} sm={4}>
              <BadgeSmallStyled>
                {connection.subject === ENGLISH
                  ? ENGLISH_CONNECTION
                  : MATH_CONNECTION}
              </BadgeSmallStyled>
              <OutcomeList>
                <UnitOutcomesList
                  outcomes={connection.outcomes || []}
                  setOutcome={outcome => setOutcomeId(outcome.id)}
                />
              </OutcomeList>
            </OutcomesCol>
          ) : null
        )}
      </OutcomesRow>
    </>
  );
};

export default TeachingAndLearningOutcomesView;
